import app from 'flarum/forum/app';
import FormModal from 'flarum/common/components/FormModal';
import Button from 'flarum/common/components/Button';
import Icon from 'flarum/common/components/Icon';
import Stream from 'flarum/common/utils/Stream';
import MainTab from './MainTab';
import ClubTab, { clubState, loadClubs, clubLocalError, clubPayload } from './ClubTab';
import { teamState, loadTeams, teamLocalError, teamPayload, needsDeletePrompt } from './TeamSection';
import { miniState, loadMiniTeams } from './MiniTeamSection';
import { bannerState, showBannerPayload } from './BannerSection';
import InfoTab, { infoState, infoPayload, infoLocalError } from './InfoTab';
import { countryKey } from '../utils/infoRules';
import { closeVis } from './Field';
import NotificationsTab, { notifState, notifPayload } from './NotificationsTab';
import ContactsTab, { contactsState, contactsPayload, contactsLocalError } from './ContactsTab';
const t = (key, params) => app.translator.trans('ppk2-profile.forum.' + key, params);

/** Tab id -> [locale key, icon]. «awards» is still a placeholder. */
export const TABS = [['main', 'tabs.main', 'fas fa-user'], ['clubTeam', 'tabs.club_team', 'fas fa-users'], ['info', 'tabs.info', 'fas fa-address-card'], ['notifications', 'tabs.notifications', 'fas fa-bell'], ['awards', 'tabs.awards', 'fas fa-trophy']];

/**
 * Footer buttons (every tab): «Выйти без сохранения» (asks first when there are unsaved
 * edits), «Сохранить» (default: primary, Enter; saves and stays open), «Сохранить и выйти»,
 * «Отменить все изменения» (back to the last saved values, stays open).
 * Immediate actions (mini-teams, «Выйти из команды», banner, photos) are already saved
 * and are not rolled back: the fields are rebuilt from the user's current attributes.
 */
export default class ProfileModal extends FormModal {
  // Esc / outside click would lose edits without asking: closing goes through exitWithoutSaving().
  static isDismissibleViaEscKey = false;
  static isDismissibleViaBackdropClick = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.user = app.session.user;
    this.tab = 'main';
    this.confirmExit = false;
    this.closing = false;
    this.state = {
      result1: null,
      // nick-check result for twink 1: { status: free|taken|invalid, message }
      result2: null,
      checking: false,
      mini: miniState(this.user.attribute('ppk2MiniTeams'))
    };
    this.resetFields();
    loadClubs(this.state.club);
    loadTeams(this.state.team);
    loadMiniTeams(this.state.mini);
  }

  /** (Re)build every saveable field from the user's current (saved) attributes. Loaded lists are kept. */
  resetFields() {
    const st = this.state;
    const prefs = this.user.preferences() || {};
    const old = {
      club: st.club,
      team: st.team,
      info: st.info
    };
    st.alt1 = Stream(this.user.attribute('ppk2AltNick1') || '');
    st.alt2 = Stream(this.user.attribute('ppk2AltNick2') || '');
    st.discloseOnline = Stream(prefs.discloseOnline !== false);
    st.hapticFeedback = Stream(!!prefs.hapticFeedback);
    st.result1 = st.result2 = null;
    st.club = clubState(this.user.attribute('ppk2Club'));
    if (old.club) {
      st.club.clubs = old.club.clubs;
      st.club.loadError = old.club.loadError;
      const ci = st.club.initial;
      if (ci && st.club.clubs && !st.club.clubs.some(c => c.id === ci.id)) st.club.clubs.push({
        id: ci.id,
        name: ci.name,
        status: ci.clubStatus
      });
    }
    st.team = teamState(this.user.attribute('ppk2Team'));
    if (old.team) {
      st.team.teams = old.team.teams;
      st.team.loadError = old.team.loadError;
      const ti = st.team.initial;
      if (ti && st.team.teams && !st.team.teams.some(x => x.id === ti.id)) st.team.teams.push({
        id: ti.id,
        name: ti.name,
        status: ti.teamStatus
      });
    }
    bannerState(st.team);
    st.team.onAnswer = () => this.onsubmit({
      preventDefault() {}
    }, this.pendingExit);
    st.info = infoState(this.user);
    if (old.info) st.info.crop = null;
    st.contacts = contactsState(this.user);
    st.notif = notifState(this.user);
  }

  /** Unsaved edits in any tab? (immediate actions are already saved and do not count) */
  isDirty() {
    const st = this.state;
    if (!st.club) return false;
    const clean = v => (v || '').trim();
    const prefs = this.user.preferences() || {};
    if (clean(st.alt1()) !== clean(this.user.attribute('ppk2AltNick1'))) return true;
    if (clean(st.alt2()) !== clean(this.user.attribute('ppk2AltNick2'))) return true;
    if (!!st.discloseOnline() !== (prefs.discloseOnline !== false)) return true;
    if (!!st.hapticFeedback() !== !!prefs.hapticFeedback) return true;
    if (Object.keys(notifPayload(st.notif)).length > 0) return true;
    if (clubPayload(st.club) !== undefined) return true;
    if (st.team.inTeam() !== !!st.team.initial) return true;
    if (teamPayload(st.team) !== undefined) return true;
    if (showBannerPayload(st.team) !== undefined) return true;
    const info = infoPayload(st.info);
    // The time zone pre-filled from the browser is not an edit of the player.
    if (st.info.autoTimezone && info.ppk2Timezone === st.info.autoTimezone) delete info.ppk2Timezone;
    if (Object.keys(info).length > 0) return true;
    return Object.keys(contactsPayload(st.contacts)).length > 0;
  }

  /** «Выйти без сохранения» (also ×): asks first when there are unsaved edits. */
  exitWithoutSaving() {
    if (this.loading) return;
    if (this.isDirty()) {
      this.confirmExit = true;
      return;
    }
    this.close();
  }
  close() {
    this.closing = true;
    this.confirmExit = false;
    super.hide();
  }

  /** The × button calls hide(): treat it as «Выйти без сохранения». */
  hide() {
    if (this.closing) return super.hide();
    this.exitWithoutSaving();
  }

  /** «Отменить все изменения»: back to the last saved values; the modal stays open. */
  revertAll() {
    if (this.loading) return;
    this.resetFields();
    this.alertAttrs = null;
    app.alerts.show({
      type: 'info'
    }, t('alerts.reverted'));
  }

  /** Esc (anywhere on the page, focus may be outside the modal): close the question, or «Выйти без сохранения». */
  oncreate(vnode) {
    super.oncreate(vnode);
    this.escHandler = e => {
      if (e.key !== 'Escape' || this.closing) return;
      e.preventDefault();
      if (this.confirmExit) this.confirmExit = false;else if (!this.state.info.crop) this.exitWithoutSaving();
      m.redraw();
    };
    document.addEventListener('keydown', this.escHandler);
  }
  onremove(vnode) {
    document.removeEventListener('keydown', this.escHandler);
    super.onremove && super.onremove(vnode);
  }

  /** Enter anywhere in the form (except a text area or a button) = «Сохранить». */
  onkeydownForm(e) {
    if (e.key !== 'Enter' || e.defaultPrevented || e.isComposing) return;
    const tag = (e.target.tagName || '').toUpperCase();
    if (tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A' || this.confirmExit || this.state.info.crop) return;
    if (e.target.type === 'file') return;
    e.preventDefault();
    this.onsubmit(e, false);
  }
  className() {
    return 'Ppk2ProfileModal Modal--large';
  }
  title() {
    return t('modal.title');
  }
  onready() {
    // Do not auto-focus the first input (it would select a twink field).
  }
  view(vnode) {
    const v = super.view(vnode);
    // Enter = «Сохранить».
    const prev = v.attrs.onkeydown;
    v.attrs.onkeydown = e => {
      if (prev) prev(e);
      this.onkeydownForm(e);
    };
    return v;
  }

  /** Switch tab (strip, carousel, swipe). */
  selectTab(id) {
    closeVis();
    this.moreOpen = false;
    this.tab = id;
  }

  /**
   * Phone: the active tab large in the centre, the previous one on the left and the next one
   * on the right (smaller, dimmer), ‹ › at the edges; it wraps around (Награды ↔ Главная).
   */
  tabCarousel() {
    const n = TABS.length;
    const i = Math.max(0, TABS.findIndex(([id]) => id === this.tab));
    const prev = TABS[(i - 1 + n) % n];
    const next = TABS[(i + 1) % n];
    const cur = TABS[i];
    const side = (tab, dir) => m("button", {
      type: "button",
      className: 'Ppk2TabCarousel-side ' + dir,
      title: t(tab[1]),
      onclick: () => this.selectTab(tab[0])
    }, m(Icon, {
      name: tab[2]
    }), m("span", {
      className: "Ppk2TabCarousel-label"
    }, t(tab[1] + '_short')));
    return m("div", {
      className: "Ppk2TabCarousel"
    }, m("button", {
      type: "button",
      className: "Ppk2TabCarousel-arrow prev",
      "aria-label": t(prev[1]),
      onclick: () => this.selectTab(prev[0])
    }, m(Icon, {
      name: "fas fa-chevron-left"
    })), side(prev, 'prev'), m("div", {
      className: "Ppk2TabCarousel-active",
      role: "tab",
      "aria-selected": "true",
      title: t(cur[1])
    }, m(Icon, {
      name: cur[2]
    }), m("span", {
      className: "Ppk2TabCarousel-label"
    }, t(cur[1] + '_short'))), side(next, 'next'), m("button", {
      type: "button",
      className: "Ppk2TabCarousel-arrow next",
      "aria-label": t(next[1]),
      onclick: () => this.selectTab(next[0])
    }, m(Icon, {
      name: "fas fa-chevron-right"
    })));
  }
  content() {
    return [m("div", {
      className: "Ppk2Tabs",
      role: "tablist"
    }, TABS.map(([id, key, icon]) => m("button", {
      type: "button",
      role: "tab",
      className: 'Ppk2Tabs-tab' + (this.tab === id ? ' active' : ''),
      "aria-selected": this.tab === id,
      title: t(key),
      onclick: () => this.selectTab(id)
    }, m(Icon, {
      name: icon
    }), m("span", {
      className: "Ppk2Tabs-full"
    }, t(key)), m("span", {
      className: "Ppk2Tabs-short"
    }, t(key + '_short'))))),
    // phone only (see .Ppk2ProfilePage CSS): looped carousel instead of the tab strip
    this.tabCarousel(), m("div", {
      className: "Modal-body Ppk2ProfileModal-body"
    }, this.tab === 'main' ? m(MainTab, {
      user: this.user,
      state: this.state,
      loading: this.loading
    }) : this.tab === 'clubTeam' ? m(ClubTab, {
      state: this.state,
      user: this.user,
      loading: this.loading
    }) : this.tab === 'info' ? [m(InfoTab, {
      state: this.state.info,
      user: this.user,
      loading: this.loading
    }),
    // «Контакты» at the bottom of «Инфо» (the set depends on the country above)
    m(ContactsTab, {
      state: this.state.contacts,
      country: countryKey(this.state.info.values.ppk2Country()),
      loading: this.loading
    })] : this.tab === 'notifications' ? m(NotificationsTab, {
      state: this.state.notif,
      user: this.user,
      loading: this.loading
    }) : m("div", {
      className: "Ppk2Placeholder"
    }, t('common.in_development'))), m("div", {
      className: "Modal-footer Ppk2ProfileModal-footer"
    }, m("div", {
      className: "Ppk2ProfileModal-hint"
    }, m(Icon, {
      name: "fas fa-circle-info"
    }), " ", t('modal.hover_hint')), m("div", {
      className: "Ppk2ProfileModal-buttons"
    }, m(Button, {
      type: "button",
      className: "Button Ppk2Btn-exit",
      icon: "fas fa-right-from-bracket",
      onclick: () => this.exitWithoutSaving(),
      disabled: this.loading
    }, t('buttons.exit_without_saving')), m(Button, {
      type: "submit",
      className: "Button Button--primary Ppk2Btn-save",
      icon: "fas fa-floppy-disk",
      loading: this.loading && !this.pendingExit
    }, t('buttons.save')), m(Button, {
      type: "button",
      className: "Button Ppk2Btn-saveExit",
      icon: "fas fa-check",
      loading: this.loading && this.pendingExit,
      disabled: this.loading,
      onclick: () => this.onsubmit({
        preventDefault() {}
      }, true)
    }, t('buttons.save_and_exit')), m(Button, {
      type: "button",
      className: "Button Ppk2Btn-revert",
      icon: "fas fa-rotate-left",
      onclick: () => this.revertAll(),
      disabled: this.loading
    }, t('buttons.revert_all')), m("button", {
      type: "button",
      className: 'Button Button--icon Ppk2Btn-more' + (this.moreOpen ? ' active' : ''),
      title: t('buttons.more'),
      "aria-label": t('buttons.more'),
      "aria-haspopup": "menu",
      "aria-expanded": this.moreOpen ? 'true' : 'false',
      onclick: () => this.moreOpen = !this.moreOpen
    }, m(Icon, {
      name: "fas fa-ellipsis"
    }))), this.moreOpen ? [m("div", {
      className: "Ppk2MoreBackdrop",
      onclick: () => this.moreOpen = false
    }), m("div", {
      className: "Ppk2MoreMenu",
      role: "menu",
      oncreate: vn => vn.dom.querySelector('button').focus({
        preventScroll: true
      }),
      onkeydown: e => e.key === 'Escape' && (e.stopPropagation(), this.moreOpen = false)
    }, [['saveExit', 'fas fa-check', 'buttons.save_and_exit', () => this.onsubmit({
      preventDefault() {}
    }, true)], ['revert', 'fas fa-rotate-left', 'buttons.revert_all', () => this.revertAll()], ['exit', 'fas fa-right-from-bracket', 'buttons.exit_without_saving', () => this.exitWithoutSaving()]].map(([id, icon, key, action]) => m("button", {
      type: "button",
      role: "menuitem",
      className: 'Ppk2MoreMenu-item Ppk2MoreMenu-item--' + id,
      disabled: this.loading,
      onclick: () => {
        this.moreOpen = false;
        action();
      }
    }, m(Icon, {
      name: icon
    }), m("span", null, t(key)))))] : null), this.confirmExit ? m("div", {
      className: "Ppk2Confirm",
      role: "alertdialog",
      "aria-modal": "true",
      oncreate: vn => vn.dom.querySelector('.Ppk2Confirm-stay').focus()
    }, m("div", {
      className: "Ppk2Confirm-box"
    }, m("div", {
      className: "Ppk2Confirm-text"
    }, m(Icon, {
      name: "fas fa-triangle-exclamation"
    }), " ", t('modal.unsaved_confirm')), m("div", {
      className: "Ppk2Confirm-buttons"
    }, m(Button, {
      type: "button",
      className: "Button Button--danger Ppk2Confirm-leave",
      onclick: () => this.close()
    }, t('buttons.exit_without_saving')), m(Button, {
      type: "button",
      className: "Button Button--primary Ppk2Confirm-stay",
      onclick: () => this.confirmExit = false
    }, t('buttons.stay'))))) : null];
  }

  /** «Сохранить» (exit = false, stays open) or «Сохранить и выйти» (exit = true). */
  onsubmit(e, exit = false) {
    e.preventDefault();
    if (this.loading) return;
    this.pendingExit = !!exit;

    // A new club with a badly written address: show it instead of sending.
    const clubError = clubLocalError(this.state.club);
    if (clubError) {
      this.tab = 'clubTeam';
      this.state.club.showErrors = true;
      app.alerts.show({
        type: 'error'
      }, app.translator.trans(clubError[0], clubError[1]));
      return;
    }

    // Info: too long text or a birth date outside 6–100 years.
    const infoError = infoLocalError(this.state.info);
    if (infoError) {
      this.tab = 'info';
      app.alerts.show({
        type: 'error'
      }, app.translator.trans(infoError[0], infoError[1]));
      return;
    }

    // Contacts: a badly written handle / phone / address in a shown field.
    const contactError = contactsLocalError(this.state.contacts, countryKey(this.state.info.values.ppk2Country()));
    if (contactError) {
      this.tab = 'info';
      app.alerts.show({
        type: 'error'
      }, contactError[1]);
      return;
    }

    // Team: no team chosen, or a captain leaving / switching (captaincy must be handed over first).
    const teamError = teamLocalError(this.state.team);
    if (teamError) {
      this.tab = 'clubTeam';
      this.state.team.showErrors = true;
      app.alerts.show({
        type: 'error'
      }, teamError);
      return;
    }

    // The captain is the last member and leaves / switches: ask «Удалить команду или оставить пустой?» first.
    const ts = this.state.team;
    const askDelete = needsDeletePrompt(ts);
    if (askDelete && !ts.deleteChoice) {
      this.tab = 'clubTeam';
      ts.prompt = true;
      return;
    }
    this.loading = true;
    this.alertAttrs = null;
    const clean = v => (v || '').trim() || null;
    const data = {
      ppk2AltNick1: clean(this.state.alt1()),
      ppk2AltNick2: clean(this.state.alt2()),
      preferences: {
        discloseOnline: !!this.state.discloseOnline()
      }
    };
    const prefs = this.user.preferences() || {};
    if (!!this.state.hapticFeedback() !== !!prefs.hapticFeedback) data.preferences.hapticFeedback = !!this.state.hapticFeedback();

    // Club: sent only when changed, so saving other tabs never resets its status.
    const club = clubPayload(this.state.club);
    if (club !== undefined) data.ppk2Club = club;
    const team = teamPayload(this.state.team);
    if (team !== undefined) data.ppk2Team = team;
    const showBanner = showBannerPayload(this.state.team);
    if (showBanner !== undefined) data.ppk2ShowTeamBanner = showBanner;

    // Info: only changed attributes (photos are uploaded / deleted immediately).
    Object.assign(data, infoPayload(this.state.info));
    // Contacts: only changed entries.
    Object.assign(data, contactsPayload(this.state.contacts));
    // «Оповещения»: Flarum's own preferences (notify_*, subscription switches) + ppk2Notifications.
    const notif = notifPayload(this.state.notif);
    if (notif.preferences) Object.assign(data.preferences, notif.preferences);
    if (notif.ppk2Notifications) data.ppk2Notifications = notif.ppk2Notifications;

    // Mini-teams are not sent here: their actions are immediate (MiniTeamSection).

    const deleteTeam = askDelete && ts.deleteChoice === 'delete';
    if (deleteTeam) data.ppk2DeleteTeam = true;
    const oldTeamName = ts.initial ? ts.initial.name : null;
    this.user.save(data).then(() => {
      this.loading = false;
      app.alerts.show({
        type: 'success'
      }, deleteTeam ? t('club_team.team.deleted', {
        name: oldTeamName
      }) : t('alerts.saved'));
      if (exit) {
        this.close();
      } else {
        // Stay open: the saved values become the new «last saved state».
        this.resetFields();
        m.redraw();
      }
    }).catch(error => {
      this.loading = false;
      ts.deleteChoice = null;
      this.onerror(error);
    });
  }
}
flarum.reg.add('ppk2-profile', 'forum/components/ProfileModal', ProfileModal);